import {
  applyTween,
  LayoutModule,
  ModuleProperties,
  scrollTo,
  useMotionOptions,
  useShowInstructions,
} from '@backstage-components/base';
import {Box, BoxProps} from '@chakra-ui/react';
import {motion} from 'framer-motion';
import React, {Fragment, useMemo, useState, VFC} from 'react';
import {useSubscription} from 'observable-hooks';
import {
  instructions,
  reactName as namespace,
  SchemaType,
} from './ButtonOverlayDefinition';
import {cx, css} from '@emotion/css';

export interface ButtonOverlayProps extends ModuleProperties, SchemaType {
  onClick?: () => void;
  isExternal?: boolean;
}

export type ButtonOverlayComponentDefinition = LayoutModule<
  'ButtonOverlay',
  ButtonOverlayProps
>;

export const TEST_ID = `${namespace}-test`;

const MotionBox = motion<BoxProps>(Box);
const MotionLink = motion<
  BoxProps & Pick<HTMLAnchorElement, 'href' | 'target'>
>(Box);

export const ButtonOverlayComponent: VFC<ButtonOverlayComponentDefinition> = (
  definition
) => {
  const {slotRenderer: Component = () => <Fragment />} = definition;
  const {props} = definition;
  const {animationStates, href} = props;
  const [activeVariant, setActiveVariant] = useState<string | undefined>(
    undefined
  );
  const motionOptions = useMotionOptions(animationStates);

  const renderedChildren = useMemo(() => {
    const {items, ...children} = definition.slots ?? {items: []};
    const components = Object.values(children)
      .flatMap((element) => {
        if (Array.isArray(element)) {
          return element;
        } else if (typeof element !== 'undefined') {
          return [element];
        } else {
          return [];
        }
      })
      .concat(items ?? []);

    return components.map((component) => {
      const componentStyle = `${component.style + ';' || ''}`;
      return (
        <Component
          key={`${component.path.join(':')}:${component.mid}`}
          {...component}
          style={componentStyle}
        />
      );
    });
  }, [Component, definition.slots]);

  const {observable, broadcast} = useShowInstructions(instructions, definition);

  const onClick = (): void => {
    if ('onClick' in props && typeof props.onClick === 'function') {
      props.onClick();
    }
    broadcast({type: `${namespace}:on-click`, meta: {}});
  };

  useSubscription(observable, (inst) => {
    if (inst.type === `${namespace}:click`) {
      onClick();
    } else if (inst.type === `${namespace}:animationState`) {
      applyTween(inst, animationStates, setActiveVariant);
    } else if (inst.type === `ButtonOverlay:scrollTo`) {
      const {elementId, anchorElId, scrollX, scrollY} = inst.meta;
      scrollTo({elementId, anchorElId, scrollX, scrollY});
    }
  });

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  return href ? (
    <MotionLink
      as="a"
      role="link"
      href={href}
      target={props?.isExternal ? '_blank' : '_self'}
      id={definition.id}
      data-testid={TEST_ID}
      className={cx(styleClassName, definition.mid)}
      {...motionOptions}
      animate={activeVariant}
      onClick={onClick}
    >
      {renderedChildren}
    </MotionLink>
  ) : (
    <MotionBox
      as="button"
      role="button"
      id={definition.id}
      data-testid={TEST_ID}
      className={cx(styleClassName, definition.mid)}
      {...motionOptions}
      animate={activeVariant}
      onClick={onClick}
    >
      {renderedChildren}
    </MotionBox>
  );
};
